import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem, Chip, Switch,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, IconButton, Drawer, Divider, FormControlLabel, Autocomplete, Tooltip,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InsightsIcon from '@mui/icons-material/Insights';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPromos, createPromo, updatePromo, deletePromo, getPromoUsage, PromoCode, PromoInput,
} from '../services/promoAdminService';
import { getAllStates, getCitiesByState } from '../services/referenceService';
import { useToast } from '../lib/useToast';
import { getApiErrorMessage } from '../lib/toast';
import { useConfirm } from '../components/feedback/ConfirmDialog';
import { useCan } from '../auth/Can';
import FormDialog from '../components/common/FormDialog';
import HelpDialog from '../components/common/HelpDialog';

const emptyForm: PromoInput = {
  code: '', description: '', discountType: 'flat', discountValue: 0,
  maxDiscount: undefined, minFare: undefined, maxRedemptions: undefined,
  perUserLimit: 1, targetRole: '', targetCity: '', validFrom: '', validUntil: '', isActive: true,
};

const PromoList: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { can } = useCan();
  const canWrite = can('promos:write');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<PromoInput>(emptyForm);
  const [usageId, setUsageId] = useState<number | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  // State -> City cascade for Target City (canonical city names, same as City Operations).
  const [statesList, setStatesList] = useState<any[]>([]);
  const [citiesList, setCitiesList] = useState<any[]>([]);
  const [selectedStateCode, setSelectedStateCode] = useState<string | null>(null);

  useEffect(() => {
    getAllStates()
      .then((res) => { if (res.data?.success) setStatesList(res.data.states); })
      .catch(() => {});
  }, []);

  const handleStateChange = async (stateCode: string | null) => {
    setSelectedStateCode(stateCode);
    setCitiesList([]);
    if (stateCode) {
      try {
        const res = await getCitiesByState(stateCode);
        if (res.data?.success) setCitiesList(res.data.cities);
      } catch { /* ignore */ }
    }
  };

  const promos = useQuery({ queryKey: ['promos'], queryFn: () => getPromos() });
  const usage = useQuery({
    queryKey: ['promo-usage', usageId],
    queryFn: () => getPromoUsage(usageId!),
    enabled: usageId != null,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['promos'] });

  const saveMut = useMutation({
    mutationFn: () => {
      // Coerce numbers + drop empty optionals.
      const payload: any = {
        ...form,
        discountValue: Number(form.discountValue),
        perUserLimit: Number(form.perUserLimit) || 1,
        maxDiscount: form.maxDiscount ? Number(form.maxDiscount) : null,
        minFare: form.minFare ? Number(form.minFare) : null,
        maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : null,
        validFrom: form.validFrom || null,
        validUntil: form.validUntil || null,
      };
      return editId ? updatePromo(editId, payload) : createPromo(payload);
    },
    onSuccess: () => {
      toast.success(editId ? 'Promo updated' : 'Promo created');
      setDialogOpen(false);
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deletePromo(id),
    onSuccess: () => { toast.success('Promo deleted'); invalidate(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const openCreate = () => { setEditId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p: PromoCode) => {
    setEditId(p.id);
    setForm({
      code: p.code, description: p.description ?? '', discountType: p.discountType,
      discountValue: p.discountValue, maxDiscount: p.maxDiscount ?? undefined,
      minFare: p.minFare ?? undefined, maxRedemptions: p.maxRedemptions ?? undefined,
      perUserLimit: p.perUserLimit, targetRole: p.targetRole ?? '', targetCity: p.targetCity ?? '',
      validFrom: p.validFrom ? p.validFrom.slice(0, 10) : '',
      validUntil: p.validUntil ? p.validUntil.slice(0, 10) : '',
      isActive: p.isActive,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (p: PromoCode) => {
    const ok = await confirm({ title: 'Delete promo', message: `Delete code ${p.code}? This cannot be undone.`, destructive: true, confirmText: 'Delete' });
    if (ok) deleteMut.mutate(p.id);
  };

  const rows: PromoCode[] = promos.data?.data ?? [];

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <LocalOfferIcon color="secondary" />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Promo Codes</Typography>
        <Tooltip title="How promo codes work">
          <IconButton size="small" onClick={() => setHelpOpen(true)}><HelpOutlineIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Box flex={1} />
        {canWrite && <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>New Promo</Button>}
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Discount</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Min Fare</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Per-user</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Used / Max</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Valid until</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {promos.isLoading && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!promos.isLoading && rows.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No promo codes yet.</TableCell></TableRow>
            )}
            {rows.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{p.code}</TableCell>
                <TableCell>
                  {p.discountType === 'percentage' ? `${p.discountValue}%` : `₹${p.discountValue}`}
                  {p.discountType === 'percentage' && p.maxDiscount ? ` (max ₹${p.maxDiscount})` : ''}
                </TableCell>
                <TableCell>{p.minFare ? `₹${p.minFare}` : '-'}</TableCell>
                <TableCell>{p.perUserLimit}</TableCell>
                <TableCell>{p.redemptionCount}{p.maxRedemptions != null ? ` / ${p.maxRedemptions}` : ''}</TableCell>
                <TableCell>
                  {[p.targetRole, p.targetCity].filter(Boolean).join(', ') || 'All'}
                </TableCell>
                <TableCell>{p.validUntil ? p.validUntil.slice(0, 10) : '-'}</TableCell>
                <TableCell>
                  <Chip size="small" label={p.isActive ? 'Active' : 'Inactive'} color={p.isActive ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <IconButton size="small" onClick={() => setUsageId(p.id)}><InsightsIcon fontSize="small" /></IconButton>
                    {canWrite && <IconButton size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" color="info" /></IconButton>}
                    {canWrite && <IconButton size="small" onClick={() => handleDelete(p)}><DeleteIcon fontSize="small" color="error" /></IconButton>}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / edit dialog */}
      <FormDialog
        open={dialogOpen}
        title={editId ? 'Edit Promo' : 'New Promo'}
        onClose={() => setDialogOpen(false)}
        onSubmit={() => saveMut.mutate()}
        submitting={saveMut.isPending}
        disableSubmit={!form.code || !form.discountValue}
        maxWidth="md"
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
          <TextField label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} fullWidth />
          <TextField select label="Discount Type" value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value as any })} fullWidth>
            <MenuItem value="flat">Flat (₹)</MenuItem>
            <MenuItem value="percentage">Percentage (%)</MenuItem>
          </TextField>
          <TextField label="Discount Value" type="number" value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value as any })} fullWidth />
          <TextField label="Max Discount (₹, % only)" type="number" value={form.maxDiscount ?? ''} onChange={(e) => setForm({ ...form, maxDiscount: e.target.value as any })} fullWidth />
          <TextField label="Min Fare (₹)" type="number" value={form.minFare ?? ''} onChange={(e) => setForm({ ...form, minFare: e.target.value as any })} fullWidth />
          <TextField label="Per-user limit" type="number" value={form.perUserLimit} onChange={(e) => setForm({ ...form, perUserLimit: e.target.value as any })} fullWidth />
          <TextField label="Max total redemptions" type="number" value={form.maxRedemptions ?? ''} onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value as any })} fullWidth />
          <TextField select label="Target Role" value={form.targetRole ?? ''} onChange={(e) => setForm({ ...form, targetRole: e.target.value })} fullWidth>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="rider">Rider</MenuItem>
            <MenuItem value="driver">Driver</MenuItem>
          </TextField>
          {/* Target city via State -> City cascade (canonical names, matching City Operations). */}
          <Autocomplete
            options={statesList}
            getOptionLabel={(o) => o.name ?? ''}
            value={statesList.find((s) => s.isoCode === selectedStateCode) || null}
            onChange={(_e, v) => handleStateChange(v ? v.isoCode : null)}
            renderInput={(params) => <TextField {...params} label="State (for Target City)" placeholder="Pick a state…" />}
            fullWidth
          />
          <Autocomplete
            options={citiesList.map((c) => c.name)}
            value={form.targetCity || null}
            onChange={(_e, v) => setForm({ ...form, targetCity: v ?? '' })}
            disabled={!selectedStateCode && !form.targetCity}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target City (blank = all cities)"
                placeholder={selectedStateCode ? 'Pick a city…' : 'Pick a state first'}
                helperText={form.targetCity ? `Current: ${form.targetCity}` : undefined}
              />
            )}
            fullWidth
          />
          <TextField label="Valid From" type="date" InputLabelProps={{ shrink: true }} value={form.validFrom ?? ''} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} fullWidth />
          <TextField label="Valid Until" type="date" InputLabelProps={{ shrink: true }} value={form.validUntil ?? ''} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} fullWidth />
          <TextField label="Description" value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth sx={{ gridColumn: { sm: '1 / -1' } }} />
          <FormControlLabel control={<Switch checked={!!form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />} label="Active" />
        </Box>
      </FormDialog>

      {/* Usage drawer */}
      <Drawer anchor="right" open={usageId != null} onClose={() => setUsageId(null)}>
        <Box sx={{ width: { xs: '100vw', sm: 460 }, p: 3 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Promo Usage</Typography>
            <IconButton onClick={() => setUsageId(null)}><CloseIcon /></IconButton>
          </Stack>
          {usage.isLoading && <CircularProgress size={24} />}
          {usage.data && (
            <>
              <Typography sx={{ fontWeight: 700, fontFamily: 'monospace', mb: 1 }}>{usage.data.promo?.code}</Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Paper sx={{ p: 1.5, flex: 1 }}><Typography variant="caption" color="text.secondary">Redemptions</Typography><Typography variant="h6">{usage.data.totalRedemptions}</Typography></Paper>
                <Paper sx={{ p: 1.5, flex: 1 }}><Typography variant="caption" color="text.secondary">Total Discount</Typography><Typography variant="h6">₹{usage.data.totalDiscount}</Typography></Paper>
              </Stack>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Recent redemptions</Typography>
              {usage.data.redemptions?.length === 0 && <Typography variant="body2" color="text.secondary">None yet.</Typography>}
              {usage.data.redemptions?.map((r: any) => (
                <Box key={r.id} sx={{ py: 0.75, borderBottom: '1px solid #eee', fontSize: 13 }}>
                  <Box sx={{ fontWeight: 600 }}>{r.userId}</Box>
                  <Box sx={{ color: 'text.secondary' }}>₹{r.discountApplied} {r.rideId ? `· ride #${r.rideId}` : ''} · {r.createdAt?.slice(0, 10)}</Box>
                </Box>
              ))}
            </>
          )}
        </Box>
      </Drawer>

      <HelpDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="How Promo Codes work"
        intro="Create a discount code with rules; the backend validates and tracks every redemption."
        warning="Heads up: code management + rules are live, but applying codes at ride booking is NOT yet wired into the pricing/checkout flow. So today you can create/manage codes and the validation engine exists — riders can't redeem them in-app until that integration ships."
        flows={[
          {
            title: 'Lifecycle',
            steps: [
              { label: 'Create a code', detail: 'Discount type (flat ₹ or %), value, optional max-discount cap.' },
              { label: 'Set rules', detail: 'Min fare, per-user limit, total redemptions cap, validity window, target role/city.' },
              { label: 'Code is active', detail: 'While isActive and inside the validity window.' },
              { label: 'Rider applies the code (future)', detail: 'Engine checks all rules and computes the capped discount.' },
              { label: 'Redemption recorded', detail: 'Per-user + global counters increment; visible in the Usage panel.' },
            ],
          },
        ]}
        notes={[
          'Percentage discounts respect the Max Discount cap; flat discounts never exceed the fare.',
          'Target City uses canonical names (State → City), matching your other tables.',
          'Usage (chart icon on each row) shows redemptions + total discount given.',
          'Managing promos needs promos:write; viewing needs promos:read.',
        ]}
      />
    </Box>
  );
};

export default PromoList;
